/**
 * Turf War's bands on the green (was TurfWarHost rolloutBandsFor), free of
 * React Native so node tests and the TV page's fixtures can build them.
 */
import type { PuttSceneRolloutBand } from '../../../../packages/putt-scene/src/SceneContent';
import { blackoutRolloutBand } from '../../../../packages/putt-scene/src/bandStates';
import {
  TURF_WAR_DEAD_ZONES,
  TURF_WAR_ZONES,
  turfWarStandings,
  type TurfWarState,
  type TurfWarZone,
} from './turfWar';
import { ADULT_DISTANCES, isShortField, type DistanceView } from './kidDistances';

/** "10–15", or Rookie's "5–7" and Pro's "5–8" (kidModes.ts TURF_WAR_LEVEL_MAPS). */
export function turfWarZoneLabel(zone: Pick<TurfWarZone, 'minFt' | 'maxFt' | 'label'>, view: DistanceView = ADULT_DISTANCES): string {
  return isShortField(view) ? `${view.ft(zone.minFt)}–${view.ft(zone.maxFt)}` : zone.label;
}

export interface TurfWarBandPalette {
  /** By player: red, then blue (TurfWarHud TURF_WAR_PLAYER_COLORS). */
  players: readonly [string, string];
  /** An unclaimed zone's line and label (colors.goldLight). */
  open: string;
}

/**
 * Five-foot scoring zones tinted by owner, and the dead gaps between them
 * shaded dark. Once a playoff has picked its zone ("20–25 FT · SUDDEN
 * DEATH"), every other zone and gap is blacked out with its label gone, so
 * the one place to aim is obvious, through the end of the playoff (Cole,
 * 2026-10-18).
 */
export function turfWarRolloutBands(state: TurfWarState, palette: TurfWarBandPalette, view: DistanceView = ADULT_DISTANCES): PuttSceneRolloutBand[] {
  const standings = turfWarStandings(state);
  const playoffZone = state.suddenDeath?.zoneIndex ?? null;
  const scoring: PuttSceneRolloutBand[] = TURF_WAR_ZONES.map((zone) => {
    const controller = standings[zone.index].controllerIndex;
    const ownerColor = controller === null ? undefined : palette.players[controller];
    const band: PuttSceneRolloutBand = {
      id: `turf-${zone.index}`,
      // A Rookie or Pro turn draws its level's zones.
      minFt: view.ft(zone.minFt),
      maxFt: view.ft(zone.maxFt),
      // The generated 3D label atlas uses the ASCII hyphen.
      label: `${view.ft(zone.minFt)}-${view.ft(zone.maxFt)} FT`,
      // Floating text projects farther downrange than its ground anchor.
      // Bring every label forward so it reads over turf, not the next dark gap.
      labelOffsetFt: isShortField(view) ? -1.5 : -4,
      // Rookie's zones are 5-23 ft, Pro's 5-32: the labels shrink and come in from the
      // edge, or the near ones fill the screen.
      ...(isShortField(view) ? { labelScale: 0.6, labelRailFt: 3 } : {}),
      fillColor: ownerColor,
      fillOpacity: ownerColor ? 0.24 : 0,
      lineColor: playoffZone === zone.index ? palette.open : ownerColor ?? palette.open,
    };
    return playoffZone !== null && playoffZone !== zone.index ? blackoutRolloutBand(band) : band;
  });
  // The gaps, painted dark so the player can SEE where not to leave it (Cole,
  // 2026-08-22: "maybe shade them like black or something"). Drawn heavier
  // than a controlled zone deliberately — a dead zone is a hard rule, and if
  // it reads as just another band the mode is back to feeling like the whole
  // green scores.
  const dead: PuttSceneRolloutBand[] = TURF_WAR_DEAD_ZONES.map((gap, index) => {
    const band: PuttSceneRolloutBand = {
      id: `turf-dead-${index}`,
      minFt: view.ft(gap.minFt),
      maxFt: view.ft(gap.maxFt),
      // No text (Cole, 2026-08-23: "remove dead labels, keep black shading").
      // The shading already says "nothing scores here"; the word repeated five
      // times down the green was reading as clutter on the putting surface.
      label: '',
      fillColor: '#05070C',
      fillOpacity: 0.55,
      lineColor: '#05070C',
    };
    return playoffZone !== null ? blackoutRolloutBand(band) : band;
  });
  return [...scoring, ...dead];
}
